
import {
  FeatureSource,
  ModelBase,
  OptionalFeatureBase,
  SmartHash,
} from "@models"
import {
  Condition,
  EldritchInvocation,
  FightingStyle,
  Spell,
  SpellSlotType,
  Table,
  Item,
  MagicItemKeyword,
  PseudoItem,
  Feat,
  GameClass,
  Subclass,
  Gift,
  Background,
  PactBoon,
  Race,
  Subrace,
  TrueCreatureInstance,
  Creature,
  SpecialFeature,
  Tool,
  WeaponMasteryProperty,
  SpellList,
  CreatureLimitation,
  RolledValue,
  CreatureGift,
  EquipmentPack,
  RollableTable,
  StandardFeatures,
  BastionFacility,
  COptions,
  Character,
  Campaign,
  CreatureInstance,
} from "@dndModels";
import {
  ALL_DRAGONS,
  CREATURE_TYPES,
  DATA_TYPE_INFO,
} from "@dndModels/Constants";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import { DataType } from "@dndModels/Types";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

// What a character's data load (loadData) fetches. Exported so character pages can track its progress.
export const core_data_types: DataType[] = [
  "spell",
  "standard_features",
  "item",
  "race","subrace",
  "game_class","subclass",
  "background", 
  "table",
  // All of these are going to be removed from this.
  // Some are getting hardcoded (done)
  // Others are getting changed to loading as needed
  // "condition",
  // "optional_feature_base",
  // "spell_slot_type",
  // "transform",
  // "eldritch_invocation", 
  // "fighting_style",
  // "gift",
  // "pact_boon", 
  // "organization",
  // "org_position",
  // "adaptation",
  // "creature_gift",
  // I'll get to these ones later. Some I'm not sure I want to do, and the others aren't big deals.
  "tool",
  "equipment_pack",
  "magic_item_keyword",
  "feat",
  "special_feature",
  "weapon_mastery_property",
  "rollable_table",
  "spell_list",
  "reusable_string"
];

export class DataUtilitiesClass {
  option_source_id: string = "";
  char_options: COptions | undefined = undefined;
  my_smart_hash: SmartHash = {};
  archived_data_holder: SmartHash = {};

  loadData = async ({ 
    char,
    onProgress,
    onError
  }: { 
    char: Character | Campaign, 
    onProgress?: ((data_type: DataType, percentage: number, preloading?: boolean) => void),
    onError?: ((message: string) => void)
  }) => {
    // debugger;
    const new_source_id = char instanceof Character && char.old_campaign_id !== "" ? char.old_campaign_id : char._id;
    if (new_source_id !== this.option_source_id) {
      this.my_smart_hash = {};
      this._base_pseudo_items = null;
      this.archived_data_holder = {};
      this.option_source_id = new_source_id;

      this.char_options = char.c_options;
      const char_options = this.char_options;
      // This doesn't wait for them. Connecting the character gets the objects it
      // uses by id (getObjectAsync), and the rest get merged into the hash around
      // them as they arrive, so they're ready when the user goes to pick something
      // else or opens another character.
      const archived_sb_ids = char_options.allow_archives ? api.source_books.filter(o =>
        o.archived && char_options.source_books.includes(o._id)).map(o => o._id) : [];
      this.core_data_loaded = false;
      const loading: Promise<void> = Promise.all(core_data_types.map(async data_type => {
        await api.loadDataType(data_type, "standard");
        for (const archive_id of archived_sb_ids) {
          const archived = await api.loadDataType(data_type, "standard", { archive_id }) as ModelBase[];
          if (this.option_source_id === new_source_id) {
            this.archived_data_holder[data_type] = [
              ...(this.archived_data_holder[data_type] ?? []),
              ...archived.filter(o => o.archived)
            ];
            delete this.my_smart_hash[data_type];
            this._base_pseudo_items = null;
          }
        }
      })).then(() => {
        if (this.core_data_loading === loading) {
          this.core_data_loaded = true;
          this._base_pseudo_items = null;
        }
      }).catch(ex => {
        console.error(ex);
        if (onError) {
          onError("Error while loading data");
        }
      });
      this.core_data_loading = loading;
    }
  }
  // Resolves once everything loadData queued up has finished loading.
  core_data_loading: Promise<void> = Promise.resolve();
  core_data_loaded = true;

  // The object with this id, run through the same character option filters as
  // getGoodDataOfType, without waiting for the rest of its data type to load.
  // Once the data type is loaded it just looks it up.
  getObjectAsync = async <T extends ModelBase>(data_type: DataType, id: string): Promise<T | undefined> => {
    if (id === "") {
      return undefined;
    }
    if (api.isDataTypeLoaded(data_type)) {
      return this.getGoodDataOfType<T>(data_type).find(o => o._id === id);
    }
    const obj = await api.getObjectById<T>(data_type, id);
    if (!obj) {
      return undefined;
    }
    // Checking dominance needs the rest of its variant group, which may not be here yet.
    return this.getGood<T>([obj], data_type, false, false).length > 0 ? obj : undefined;
  }

  getAllDataOfType<T extends ModelBase>(data_type: DataType, log_it = false): T[] {
    if (log_it) {
      debugger;
    }
    if (this.my_smart_hash[data_type] && this.my_smart_hash[data_type].length > 0) {
      return this.my_smart_hash[data_type] as T[];
    } else {
      // if (data_type === "race") {
      //   debugger;
      // }
      // Until the data type has finished loading, this is whatever has made it into
      // the hash so far (including anything getObjectAsync fetched on its own), 
      // so it isn't cached.
      const loaded = api.isDataTypeLoaded(data_type);
      let objects = [...api.getDataTypeFromHash(data_type)];
      if (this.char_options) {
        const char_options = this.char_options;
        const source_book_ids = char_options.source_books;
        const blocked_ids = char_options.blocked;
        objects = objects.filter(o => 
          common_util.anyOverlaps(o.source_book_ids, source_book_ids) && 
          !blocked_ids.includes(o._id) && 
          !blocked_ids.includes(o.variant_group_id)
        );
        if (this.archived_data_holder[data_type]) {
          // One of these may have already been fetched into the hash on its own.
          const ids = new Set(objects.map(o => o._id));
          const archived = this.archived_data_holder[data_type].filter(o => !ids.has(o._id));
          objects.push(...archived);
        }
        if (!char_options.world_settings.includes("Any")) {
          const world_settings = char_options.world_settings;
          objects = objects.filter(o => o.world_settings.length === 0 || common_util.anyOverlaps(world_settings, o.world_settings));
        }
      }
      if (loaded) {
        this.my_smart_hash[data_type] = objects;
      }
      return objects as T[];
    }
  }

  getGoodDataOfType<T extends ModelBase>(data_type: DataType, log_it = false) {
    let objects = this.getAllDataOfType<T>(data_type, log_it);

    objects = this.getGood<T>(objects, data_type, log_it); // || data_type === "game_class"
    return objects;
  }

  getGoodDataOfTypeAsync = async <T extends ModelBase>(data_type: DataType, log_it = false): Promise<T[]> => {
    if (log_it) {
      debugger;
    }
    if (core_data_types.includes(data_type)) {
      if (!api.isDataTypeLoaded(data_type)) {
        // loadData queued it, but something needs all of it now.
        await api.loadDataType(data_type, "priority");
      }
      return this.getGoodDataOfType<T>(data_type, log_it);
    } else {
      let objects = await api.getObjects<T>({ data_type, log_it });

      objects = this.getGood<T>(objects, data_type, log_it);
      return objects;
    }
  }

  getGood = <T extends ModelBase>(objects: ModelBase[], data_type: DataType, log_it = false, check_dominance = true): T[] => {
    let objects2 = [...objects];
    if (this.char_options) {
      if (log_it) {
        console.log(objects2);
        console.log(objects2.map(o => o.true_name));
      }
      const char_options = this.char_options;
      if (!char_options.allow_3rd_party) {
        objects2 = objects2.filter(o => 
          !o.source_books_old.some(o2 => o2.third_party)
        );
        if (log_it) {
          console.log(objects2);
          console.log(objects2.map(o => o.true_name));
        }
      }
      if (!char_options.allow_UA) {
        objects2 = objects2.filter(o => 
          !o.source_books_old.some(o2 => o2.type === "Unearthed Arcana")
        );
        if (log_it) {
          console.log(objects2);
          console.log(objects2.map(o => o.true_name));
        }
      }
      if (!char_options.allow_archives) {
        objects2 = objects2.filter(o => 
          !o.archived
        );
        if (log_it) {
          console.log(objects2);
          console.log(objects2.map(o => o.true_name));
        }
      }
      if (!char_options.allow_homebrew) {
        objects2 = objects2.filter(o => 
          !o.source_books_old.some(o2 => o2.type === "Homebrew")
        );
        if (log_it) {
          console.log(objects2);
          console.log(objects2.map(o => o.true_name));
        }
      }
      if (!char_options.allow_monster_feature_sources) {
        objects2 = objects2.filter(o => 
          !o.monster
        );
        if (log_it) {
          console.log(objects2);
          console.log(objects2.map(o => o.true_name));
        }
      }
      if (!char_options.world_settings.includes("Any")) {
        const world_settings = char_options.world_settings;
        objects2 = objects2.filter(o => o.world_settings.length === 0 || common_util.anyOverlaps(world_settings, o.world_settings));
        if (log_it) {
          console.log(objects2);
          console.log(objects2.map(o => o.true_name));
        }
      }
      const source_book_ids = char_options.source_books;
      const blocked_ids = char_options.blocked;
      objects2 = objects2.filter(o => 
        common_util.anyOverlaps(o.source_book_ids, source_book_ids) && 
        !blocked_ids.includes(o._id) && 
        !blocked_ids.includes(o.variant_group_id)
      );
      if (log_it) {
        console.log(objects2);
        console.log(objects2.map(o => o.true_name));
      }
      if (check_dominance && char_options.dominant_variants_only.length > 0) {
        const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
        if (log_it) {
          console.log(dt_info);
          if (dt_info) {
            console.log(dt_info.dominance_group);
            console.log(char_options.dominant_variants_only);
            console.log(char_options.dominant_variants_only.includes(dt_info.dominance_group));
          }
        }
        if (dt_info && char_options.dominant_variants_only.includes(dt_info.dominance_group)) {
          // if (dt_info.data_type === "race") {
          //   debugger;
          // }
          if (log_it) {
            console.log(objects2);
            console.log(objects2.map(o => o.true_name));
          }
          // WE NEED TO DO DOMINANCE DIFFERENT HERE. 
          // IT NEEDS TO CHECK THROUGH EACH VARIANT GROUP AND JUST TAKE THE MOST DOMINANT ONE FROM THE GROUP.
          // IT MIGHT NOT BE TRULY DOMINANT, BUT IT"S THE MOST DOMINANT THAT'S INCLUDED.
          objects2 = [...this.getAllDominants(objects, log_it),...objects.filter(o => o.isNonVersionVariant())];
          if (log_it) {
            console.log(objects2);
            console.log(objects2.map(o => o.true_name));
          }
        }
      }
    }
    return objects2 as T[];
  }
  
  // Loaded ahead
  get spells() {
    return this.getGoodDataOfType<Spell>("spell");
  }
  get all_spells() {
    return this.getAllDataOfType<Spell>("spell");
  }
  get tables() {
    const tables = this.getGoodDataOfType<Table>("table");
    return tables;
  }
  get items() {
    return this.getGoodDataOfType<Item>("item");
  }
  get races() {
    return this.getGoodDataOfType<Race>("race");
  }
  get subraces() {
    return this.getGoodDataOfType<Subrace>("subrace");
  }
  get classes() {
    return this.getGoodDataOfType<GameClass>("game_class");
  }
  get subclasses() {
    return this.getGoodDataOfType<Subclass>("subclass");
  }
  get backgrounds() {
    return this.getGoodDataOfType<Background>("background");
  }
  get standard_features() {
    return this.getGoodDataOfType<StandardFeatures>("standard_features");
  }
  get pact_boons() {
    return this.getGoodDataOfType<PactBoon>("pact_boon");
  }

  // Loaded as needed
  optional_feature_bases = async () => {
    return await api.getObjects<OptionalFeatureBase>({ data_type: "optional_feature_base" });
  }
  getConditions = async () => {
    return await this.getGoodDataOfTypeAsync<Condition>("condition");
  }
  // This is just used in value() and encumbrance
  get conditions() {
    return this.getGoodDataOfType<Condition>("condition");
  }
  getEldritchInvocations = async () => {
    return await this.getGoodDataOfTypeAsync<EldritchInvocation>("eldritch_invocation");
  }
  getPactBoons = async () => {
    return await this.getGoodDataOfTypeAsync<PactBoon>("pact_boon");
  }
  getFightingStyles = async () => {
    return await this.getGoodDataOfTypeAsync<FightingStyle>("fighting_style");
  }
  getGifts = async () => {
    return await this.getGoodDataOfTypeAsync<Gift>("gift");
  }
  // This is just used in value()
  get gifts() {
    return this.getGoodDataOfType<Gift>("gift");
  }
  getBastionFacilities = async () => {
    return await this.getGoodDataOfTypeAsync<BastionFacility>("bastion_facility");
  }
  getSpellSlotTypes = async () => {
    return await this.getGoodDataOfTypeAsync<SpellSlotType>("spell_slot_type");
  }
  // Only used in situations where we can be fairly certain that getSpellSlotTypes has already been run.
  get spell_slot_types() {
    return this.getGoodDataOfType<SpellSlotType>("spell_slot_type");
  }
  get spell_lists() {
    return this.getGoodDataOfType<SpellList>("spell_list");
  }
  getCreatureGifts = async () => {
    return await this.getGoodDataOfTypeAsync<CreatureGift>("creature_gift");
  }
  get rollable_tables() {
    return this.getGoodDataOfType<RollableTable>("rollable_table");
  }
  get equipment_packs() {
    return this.getGoodDataOfType<EquipmentPack>("equipment_pack");
  }
  get magic_item_keywords() {
    return this.getGoodDataOfType<MagicItemKeyword>("magic_item_keyword");
  }
  get feats() {
    return this.getGoodDataOfType<Feat>("feat");
  }
  get special_features() {
    return this.getGoodDataOfType<SpecialFeature>("special_feature");
  }
  get tools() {
    return this.getGoodDataOfType<Tool>("tool");
  }
  get weapon_mastery_properties() {
    return this.getGoodDataOfType<WeaponMasteryProperty>("weapon_mastery_property");
  }
  // Built the first time it's needed instead of on every load, since that's slow.
  // It's only kept once the core data is done loading, and gets rebuilt whenever that data changes.
  _base_pseudo_items: PseudoItem[] | null = null;
  get base_pseudo_items(): PseudoItem[] {
    if (this._base_pseudo_items !== null) {
      return this._base_pseudo_items;
    }
    const built = this.buildBasePseudoItems();
    if (this.core_data_loaded) {
      this._base_pseudo_items = built;
    }
    return built;
  }
  pseudo_items: PseudoItem[] = [];

  getTableAsync = async (fs: FeatureSource) => { // }, edition_id: string) {
    return await api.getTableAsync(fs);
  }

  loadObject = async (obj: ModelBase) => {
    await api.loadObject(obj);
  }

  get all_feature_sources(): FeatureSource[] {
    let eis: EldritchInvocation[] = [];
    if (this.my_smart_hash.eldritch_invocation) {
      eis = this.my_smart_hash.eldritch_invocation as EldritchInvocation[];
    }
    let pbs: PactBoon[] = [];
    if (this.my_smart_hash.pact_boon) {
      pbs = this.my_smart_hash.pact_boon as PactBoon[];
    }
    let gifts: Gift[] = [];
    if (this.my_smart_hash.gift) {
      gifts = this.my_smart_hash.gift as Gift[];
    }
    let special_features: SpecialFeature[] = [];
    if (this.my_smart_hash.special_feature) {
      special_features = this.my_smart_hash.special_feature as SpecialFeature[];
    }
    let feats: Feat[] = [];
    if (this.my_smart_hash.feat) {
      feats = this.my_smart_hash.feat as Feat[];
    }
    let magic_item_keywords: MagicItemKeyword[] = [];
    if (this.my_smart_hash.magic_item_keyword) {
      magic_item_keywords = this.my_smart_hash.magic_item_keyword as MagicItemKeyword[];
    }
    let fighting_styles: FightingStyle[] = [];
    if (this.my_smart_hash.fighting_style) {
      fighting_styles = this.my_smart_hash.fighting_style as FightingStyle[];
    }
    return [
      ...this.backgrounds, ...this.races, ...this.subraces, 
      ...this.items, ...this.classes, ...this.subclasses,
      ...special_features, ...feats, ...magic_item_keywords, 
      ...eis, ...pbs, ...gifts,
      ...fighting_styles
    ];
  }

  // pseudo_items_built: boolean = false; // This flag is to just make sure it did this.  A weird bug has popped up that is making it so it's not hitting that part of the code when it should.  It's skipping it and then hitting it later.
  buildBasePseudoItems() {
    // console.log("Building Pseudo Items");
    // Goes through this.items, copying them all into this.base_pseudo_items,
    // but if the item uses extras then it does it for each extra.
    // Change everything that uses this.items to use this.base_pseudo_items instead
    const built: PseudoItem[] = [];
    for (const item of this.items) {
      if (item.extra_base_type === "" || item.extra_base_type === "None") {
        const pseudo_item = new PseudoItem(item);
        built.push(pseudo_item);
      } else {
        if (item.extra_base_type === "Spell") {
          const extra_base_limits = item.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
          let spell_levels: number[] = [];
          if (extra_base_limits.levels.includes("Any")) {
            spell_levels = [0,1,2,3,4,5,6,7,8,9];
          } else {
            spell_levels = extra_base_limits.levels.map(o => {
              if (o === "Cantrip") {
                return 0;
              } else {
                return +o;
              }
            });
          }
          const spells = this.spells.filter(o => 
            spell_levels.includes(o.level) && 
            (extra_base_limits.schools.includes("Any") || 
              extra_base_limits.schools.includes(o.school)));
          for (const spell of spells) {
            const pseudo_item = new PseudoItem(item, spell._id, spell);
            built.push(pseudo_item);
            // const new_pseudo_item = pseudo_item.clone();
            // new_pseudo_item.add_keyword(item, spell._id, spell);
            // pseudo_items.push(new_pseudo_item);
          }
        } else if (item.extra_base_type === "Dragon Breed") {
          const extra_base_limits = item.extra_base_limits;
          const dragon_breeds = extra_base_limits.length > 0 ? extra_base_limits : ALL_DRAGONS;
          for (const breed of dragon_breeds) {
            const pseudo_item = new PseudoItem(item, breed);
            built.push(pseudo_item);
            // const new_pseudo_item = pseudo_item.clone();
            // new_pseudo_item.add_keyword(item, breed);
            // pseudo_items.push(new_pseudo_item);
          }
        } else if (item.extra_base_type === "Creature Type") {
          const extra_base_limits = item.extra_base_limits;
          const creature_types = extra_base_limits.length > 0 ? extra_base_limits : CREATURE_TYPES;
          for (const c_type of creature_types) {
            const pseudo_item = new PseudoItem(item, c_type);
            built.push(pseudo_item);
            // const new_pseudo_item = pseudo_item.clone();
            // new_pseudo_item.add_keyword(item, c_type);
            // pseudo_items.push(new_pseudo_item);
          }
        } else if (item.extra_base_type === "Damage Type") {
          console.error("Not Yet Implemented");
        } else if (item.extra_base_type === "Strings") {
          console.error("Not Yet Implemented");
        } else {
          console.error("Not Yet Implemented");
          debugger;
        }
      }
    }
    // this.pseudo_items_built = true;
    return built;
  }

  getObjects = async (type: string, filter: any = {}) => {
    let return_us: ModelBase[] = [];
    switch (type) {
      case "condition": case "Condition": return_us = await this.getConditions(); break;
      case "spell": case "Spell": return_us = this.spells; break;
      case "eldritch_invocation": case "Eldritch Invocation": return_us = await this.getEldritchInvocations(); break;
      case "spell_slot_type": case "Spell Slot Type": return_us = await this.getSpellSlotTypes(); break;
      case "fighting_style": case "Fighting Style": return_us = await this.getFightingStyles(); break;
      case "base_item": case "Base Item": case "item": case "Item": return_us = this.items; break;
      // case "magic_item": case "Magic Item": return_us = this.magic_items; break;
      case "magic_item_keyword": case "Magic Item Keyword": return_us = this.magic_item_keywords; break;
      case "feat": case "Feat": return_us = this.feats; break;
      case "table": case "Table": return_us = this.tables; break;
      case "special_feature": case "Special Feature": return_us = this.special_features; break;
      case "gift": case "Gift": return_us = await this.getGifts(); break;
      case "race": case "Race": return_us = this.races; break;
      case "subrace": case "Subrace": return_us = this.subraces; break;
      case "game_class": case "Class": return_us = this.classes; break;
      case "subclass": case "Subclass": return_us = this.subclasses; break;
      case "background": case "Background": return_us = this.backgrounds; break;
      case "pact_boon": case "Pact Boon": return_us = await this.getPactBoons(); break;
      case "tool": case "Tool": return_us = this.tools; break;
      // case "adaptation": case "Adaptation": return_us = await this.getAdaptations(); break;
      case "bastion_facility": case "Bastion Facility": return_us = await this.getBastionFacilities(); break;
      case "weapon_mastery_property": return_us = this.weapon_mastery_properties; break;
    }
    if (Object.keys(filter).length > 0) {
      return_us = api.filter("", return_us, filter); // , type.includes("ransform"));
    }
    return return_us;
  }
  
  getCreatureGiftsForCreature = async (creature: TrueCreatureInstance) => {
    const creature_gifts = await this.getCreatureGifts();
    for (const cg of creature_gifts) {
      await this.loadObject(cg);
    }
    return creature_gifts.filter(o => this.fitCreatureLimit(creature, o.limit) || creature.creature_gifts.filter(o2 => o2.source_id === o._id).length > 0);
  }

  fitCreatureLimit(ci: TrueCreatureInstance | Creature | CreatureInstance, limit: CreatureLimitation, log_it = false): boolean {
    // log_it = item.name.includes("Holding"); // true;
    if (log_it) {
      console.log(limit);
    }
    if (limit.creature_ids.length > 0) {
      if (ci instanceof Creature) {
        return limit.creature_ids.includes(ci._id);
      } else {
        return limit.creature_ids.includes(ci.creature_id);
      }
    } 
    let good = true;
    if (good && limit.creature_types.length > 0) {
      good = limit.creature_types.includes(ci.creature_type);
    } 
    if (good && limit.subtypes.length > 0) {
      good = common_util.anyOverlaps(limit.subtypes, ci.subtypes);
    } 
    if (good && limit.tags.length > 0) {
      good = common_util.anyOverlaps(limit.tags, ci.tags);
    }
    if (good && limit.age_categories.length > 0) {
      good = limit.age_categories.includes(ci.age_category);
    } 
    if (good && limit.size_nums.length > 0) {
      if (ci instanceof Creature) {
        good = limit.size_nums.includes(ci.size_num);
      } else {
        good = limit.size_nums.includes(ci.mind.use_size);
      }
    } 
    if (good && limit.min_challenge_rating > -1) {
      good = limit.min_challenge_rating <= ci.challenge_rating;
    } 
    if (good && limit.max_challenge_rating > -1) {
      good = limit.max_challenge_rating >= ci.challenge_rating;
    } 
    if (good && !limit.allow_flight) {
      good = !ci.flying;
    }
    if (good && !limit.allow_swimming) {
      good = !ci.swimming;
    }
    if (good) {
      good = limit.fitsDamageTypes(ci);
    }
    return good;
  }

  getSpell(id: string) {
    let obj_finder = this.spells.find(o => o._id === id);
    if (!obj_finder) {
      // It's probably archived, and we're not including it.  
      // Look for a dominant variant.
      obj_finder = this.getDominantSpell(id); // this.spells.filter(o => o.variant_ids.includes(char_spell.spell_id) && this.is_dominant(o));
    }
    return obj_finder;
  }

  getDominantSpell(id: string) {
    let obj_finder = this.spells.filter(o => o._id === id || o.variant_ids.includes(id));
    if (obj_finder.length > 1) {
      // Check for a dominant variant
      const dominant_variant = this.getDominant(obj_finder); // (obj_finder.variant_group as Spell[]).find(o => this.is_dominant(o));
      if (dominant_variant) {
        return dominant_variant;
      }
    }
    return obj_finder[0];
  }

  getSpellVariants(id: string) {
    return this.spells.filter(o => o._id === id || o.variant_ids.includes(id));
  }

  spellOnASpellList(spell_id: string, spell_list_ids: string[]) {
    const spell = this.spells.find(o => o._id === spell_id);
    if (spell) {
      if (spell_list_ids.length === 0 || spell_list_ids.includes("Any")) {
        return true;
      }
      return this.spell_lists.filter(o => 
        spell_list_ids.includes(o._id) && 
        api.fitSpellList({ spell, spell_list: o })
      ).length > 0;
    }
    return false;
  }

  totalRolledValue(rolled_values: RolledValue[]) {
    let total = 0;
    for (const rvs of rolled_values) {
      for (const rv of rvs.roll_values) {
        for (const v of rv.main_values) {
          total += v;
        }
      }
      total += rvs.flat;
    }
    return total;
  }

  variant_group(obj: ModelBase) {
    return this.getAllDataOfType(obj.data_type).filter(o => o.variant_group_id === obj.variant_group_id);
  }

  getDominant<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    if (objects.length === 0) {
      return undefined;
    } else if (objects.length === 1) {
      return objects[0] as T;
    } else {
      let max = Math.max(...objects.map(o => o.getCoreDominanceLevel()));
      if (max > -1) {
        return objects.find(o => o.getCoreDominanceLevel() === max) as T;
      } else {
        max = Math.max(...objects.map(o => o.getNonCoreDominanceLevel()));
        if (max > -1) {
          return objects.find(o => o.getNonCoreDominanceLevel() === max) as T;
        }
        return objects[0] as T;
      }
    }
  }

  isDominant(obj: ModelBase) {
    const dom = this.getDominantFromObject(obj);
    return dom._id === obj._id;
  }

  getVariantGroupFromObject<T extends ModelBase>(obj: T, log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    return this.getGoodDataOfType(obj.data_type).filter(o => o.variant_group_id === obj.variant_group_id);
  }

  getVariantsFromObject<T extends ModelBase>(obj: T, log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const objects = this.getVariantGroupFromObject(obj);
    return this.getVariants(objects) as T[];
  }

  /**
   * This goes through each variant group in objects, and only returns the variants, not the dominants.
   */
  getAllVariants<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const variant_groups = this.getVariantGroups(objects);
    return variant_groups.flatMap(g => this.getVariants(g));
  }
  /**
   * This goes through each variant group in objects, and only returns the non version variants, not the dominants and not the core variants.
   */
  getAllNonVersionVariants<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const variant_groups = this.getVariantGroups(objects);
    return variant_groups.flatMap(g => this.getNonVersionVariants(g));
  }
  /**
   * This goes through each variant group in objects, and only returns the dominants, not the variants.
   */
  getAllDominants<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const variant_groups = this.getVariantGroups(objects);
    return variant_groups.map(g => this.getDominant(g, log_it && g.length > 0 && g[0].name === "Paladin")).filter(o => o !== undefined) as T[];
  }
  /**
   * This goes through all the objects and splits them into their variant groups.
   */
  getVariantGroups<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const variant_group_ids = common_util.unique_array(objects.map(o => o.variant_group_id));
    return variant_group_ids.map(id => objects.filter(o => o.variant_group_id === id));
  }

  /**
   * This one assumes that the objects are all part of the same variant group.
   * It doesn't check, because it should only be called by code that does it properly.
   */
  getVariants<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const dom = this.getDominant(objects);
    if (dom) {
      return objects.filter(o => o._id !== dom._id) as T[];
    }
    return [];
  }
  getNonVersionVariants<T extends ModelBase>(objects: T[], log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const dom = this.getDominant(objects);
    if (dom) {
      return objects.filter(o => o._id !== dom._id && o.isNonVersionVariant()) as T[];
    }
    return [];
  }

  getDominantFromObject<T extends ModelBase>(obj: T, log_it: boolean = false) {
    if (log_it) {
      debugger;
    }
    const objects = this.getVariantGroupFromObject(obj);
    const dom = this.getDominant(objects);
    if (dom) {
      return dom as T;
    }
    return obj;
  }

  renderChallengeRating(challenge_rating: number) {
    if (challenge_rating > 0) {
      if (challenge_rating >= 1) {
        return ` (CR: ${challenge_rating})`;
      } else if (challenge_rating === 0.125) {
        return ` (CR: 1/8)`;
      } else if (challenge_rating === 0.25) {
        return ` (CR: 1/4)`;
      } else if (challenge_rating === 0.5) {
        return ` (CR: 1/2)`;
      }
    }
    return "";
  }

  variant_of_name(obj: ModelBase) {
    // if (this.name.includes("Aberrant")) {
    //   console.log(this.variant_group);
    // }
    const variant_group = this.variant_group(obj);
    const dominant_variant = variant_group.find(o => o.isDominant());
    if (dominant_variant && dominant_variant._id !== obj._id) {
      return dominant_variant.name;
    }
    return "";
  }

  getVariantGroupId(data_type: DataType, id: string) {
    const data = this.getGoodDataOfType(data_type);
    if (data.length > 0) {
      let obj = data.find(o => o._id === id);
      if (!obj) {
        obj = data.find(o => o.variant_ids.includes(id));
      }
      if (obj) {
        return obj.variant_group_id;
      }
    }
    return "";
  }

  getVariantGroupIds(data_type: DataType, ids: string[]) {
    return ids.map(id => this.getVariantGroupId(data_type, id));
  }
}
